/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";

import { BEM } from "@adapptio/lib-components";
import { useForkRef } from "../../../hooks";
import * as attributes from "../Dnd/dndAttributes";
import { useDndContext } from "../Dnd/DndContext";
import { IDndDraggableElProps, IDndDraggableInfo, IDndDragPoint, TDndDraggableID } from "../Dnd/DndTypes";
import { getDraggableInfo } from "../Dnd/utils";
import { useDndDropAreaContext } from "../DndDropArea/DndDropAreaContext";
import { useDeepCompareMemo } from "use-deep-compare";
import { DndDraggableContext } from "./DndDraggableContext";
import { overrideForwardRef } from "../OverridableComponent";

export interface IDndDraggableProps {
	className?: string;
	draggableId: TDndDraggableID;
	index: number;
	children?: React.ReactNode;
	disabled?: boolean;
}

const toPx = (value: number) => `${value}px`;

export const DndDraggable = overrideForwardRef<IDndDraggableProps, "div">(
	({ Component, children, className, draggableId, index, disabled, ...props }, ref) => {
		const baseClass = BEM("edt-dnd-draggable");
		const { contextId, registry } = useDndContext();
		const { droppableId, draggignOver } = useDndDropAreaContext();

		const [ { dragging, moveBy }, setDragState ] = useState<{
			dragging?: IDndDraggableInfo;
			moveBy?: IDndDragPoint;
		}>({});

		const style = useDeepCompareMemo<React.CSSProperties>(() => {
			if (dragging) {
				const x = toPx(moveBy?.x || 0);
				const y = toPx(moveBy?.y || 0);
				const top = toPx(dragging.box.contentBox.top);
				const left = toPx(dragging.box.contentBox.left);
				const width = toPx(dragging.box.contentBox.width);
				const height = toPx(dragging.box.contentBox.height);

				return {
					position: "fixed",
					top,
					left,
					width,
					height,
					zIndex: 9999999,
					transform: `translate(${x}, ${y})`,
					transition: "none",
					pointerEvents: "none",
					userSelect: "none"
				};
			}

			if (moveBy?.x || moveBy?.y) {
				const x = toPx(moveBy?.x || 0);
				const y = toPx(moveBy?.y || 0);
				return {
					transform: `translate(${x}, ${y})`,
					transition: "transform 60ms ease",
					pointerEvents: "none"
				};
			}

			if (draggignOver) {
				return {
					pointerEvents: "none"
				};
			}

			return {};
		}, [ dragging, moveBy, draggignOver ]);

		const draggableRef = useRef<HTMLDivElement>();
		const forkRef = useForkRef(draggableRef, ref);

		const draggableProps = useMemo<IDndDraggableElProps>(
			() => ({
				[attributes.draggable.draggableId]: draggableId,
				[attributes.draggable.contextId]: contextId,
				style
			}),
			[ style, draggableId, contextId ]
		);

		useEffect(() => {
			registry.draggable.register({
				id: draggableId,
				droppableId,
				index,
				getInfo: () => {
					return getDraggableInfo({ draggableId, el: draggableRef.current });
				},
				onDragMove: ({ initialPoint, currentPoint, dragging, draggingOver }) => {
					setDragState((prevState) => {
						const movedByX = currentPoint.x - initialPoint.x;
						const movedByY = currentPoint.y - initialPoint.y;

						if (dragging.draggableId === draggableId) {
							return {
								dragging,
								moveBy: {
									x: movedByX,
									y: movedByY
								}
							};
						}

						if (draggingOver === droppableId) {
							const rect = draggableRef.current.getBoundingClientRect();
							const movedY = rect.y - (prevState.moveBy?.y || 0);
							const transitionFinished = rect.y === movedY;
							const middleY =
								rect.y +
								rect.height / 2 -
								(transitionFinished ? prevState.moveBy?.y || 0 : 0);

							const draggedPoint = dragging?.box?.contentBox?.center;

							const newDraggedCenter = {
								x: draggedPoint.x + movedByX,
								y: draggedPoint.y + movedByY
							};

							if (newDraggedCenter.y < middleY || newDraggedCenter.y < rect.y) {
								return {
									dragging: null,
									moveBy: {
										x: 0,
										y: dragging.box.borderBox.height
									}
								};
							}
						}

						return {
							dragging: null,
							moveBy: null
						};
					});

					return;
				},
				onDragDrop: () => {
					setDragState({
						dragging: null,
						moveBy: null
					});
				}
			});

			return () => registry.draggable.unregister(draggableId);
		}, [ draggableId, droppableId, index ]);

		if (className) {
			baseClass.add(className);
		}

		return (
			<DndDraggableContext.Provider value={{ dndContextId: contextId, draggableId, disabled }}>
				<Component {...props} {...draggableProps} className={baseClass.toClassName()} ref={forkRef}>
					{children}
				</Component>
			</DndDraggableContext.Provider>
		);
	}
);

DndDraggable.defaultProps = {
	Component: "div"
};
